import React, { useEffect, useState } from "react";
import { getQuizLeaderboard, LeaderboardEntry, SharedQuizMeta } from "../api/gameApi";

interface CustomQuizLobbyProps {
  quiz: SharedQuizMeta;
  joinCode: string;
  error: string | null;
  onStart: () => void | Promise<void>;
  onBack: () => void;
}

const CustomQuizLobby: React.FC<CustomQuizLobbyProps> = ({ quiz, joinCode, error, onStart, onBack }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [currentUserEntry, setCurrentUserEntry] = useState<LeaderboardEntry | null>(null);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadLeaderboard = async () => {
      setIsLoadingLeaderboard(true);
      setLeaderboardError(null);
      try {
        const res = await getQuizLeaderboard(quiz.id, 5, 'all_time', 'score');
        if (!cancelled) {
          setLeaderboard(res.results);
          setCurrentUserEntry(res.current_user);
        }
      } catch {
        if (!cancelled) setLeaderboardError('Could not load the leaderboard for this game.');
      } finally {
        if (!cancelled) setIsLoadingLeaderboard(false);
      }
    };

    loadLeaderboard();
    return () => {
      cancelled = true;
    };
  }, [quiz.id]);

  const attemptsRemaining =
    quiz.attempts_allowed === null || quiz.attempts_allowed === undefined
      ? null
      : Math.max(0, quiz.attempts_allowed - (quiz.attempts_used ?? 0));

  const outOfAttempts = attemptsRemaining !== null && attemptsRemaining <= 0;

  const showOwnRow =
    currentUserEntry && !leaderboard.some((e) => e.session_id === currentUserEntry.session_id);

  const handleStartClick = async () => {
    if (isStarting || outOfAttempts) return;
    setIsStarting(true);
    try {
      await onStart();
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center px-4 py-10"
      style={{ backgroundImage: "url('/backgrounds/winning-1.png')" }}
    >
      <div
        className="w-full max-w-2xl text-white rounded-2xl p-8 shadow-2xl"
        style={{
          background: 'rgba(27, 42, 65, 0.55)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wide text-amber-200/80 font-caudex">
            Custom Game Lobby
          </span>
          <span className="text-xs text-white/50 font-mono">{joinCode.toUpperCase()}</span>
        </div>

        <h2 className="text-2xl font-bold mb-2 font-caudex">{quiz.title || 'Custom Game'}</h2>
        {quiz.description ? (
          <p className="text-sm text-white/70 mb-4 font-caudex">{quiz.description}</p>
        ) : null}

        <div className="flex flex-wrap gap-3 mb-6 text-sm font-caudex">
          <span className="px-3 py-1 rounded-full border border-cyan-200/40 text-cyan-100">
            {quiz.rounds} round{quiz.rounds !== 1 ? 's' : ''} × {quiz.questions_per_round} question{quiz.questions_per_round !== 1 ? 's' : ''}
          </span>
          <span className="px-3 py-1 rounded-full border border-cyan-200/40 text-cyan-100">
            {quiz.question_count} question{quiz.question_count !== 1 ? 's' : ''} in pool
          </span>
          <span
            className={`px-3 py-1 rounded-full border ${
              outOfAttempts ? 'border-red-300/60 text-red-200' : 'border-cyan-200/40 text-cyan-100'
            }`}
          >
            {attemptsRemaining === null
              ? 'Unlimited attempts'
              : `${attemptsRemaining} attempt${attemptsRemaining !== 1 ? 's' : ''} left`}
          </span>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-3 font-caudex text-white/80">Leaderboard for this game</h3>
          {isLoadingLeaderboard ? (
            <p className="text-sm text-white/50 font-caudex">Loading leaderboard...</p>
          ) : leaderboardError ? (
            <p className="text-sm text-amber-200 font-caudex">{leaderboardError}</p>
          ) : leaderboard.length === 0 ? (
            <p className="text-sm text-white/50 font-caudex">No one has played this game yet — be the first!</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry) => (
                <div
                  key={entry.session_id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#27315D] text-sm font-caudex"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-6 text-white/50">{entry.rank}</span>
                    <span>{entry.display_name}</span>
                  </span>
                  <span className="flex flex-col items-end leading-tight">
                    <span className="text-cyan-200 font-semibold">{entry.score}</span>
                    {entry.questions_total ? (
                      <span className="text-[11px] text-white/50">{entry.questions_correct}/{entry.questions_total}</span>
                    ) : null}
                  </span>
                </div>
              ))}

              {showOwnRow && currentUserEntry ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-teal-900/40 border border-teal-400/40 text-sm font-caudex mt-1">
                  <span className="flex items-center gap-3">
                    <span className="w-6 text-teal-200">{currentUserEntry.rank}</span>
                    <span className="text-teal-100">You</span>
                  </span>
                  <span className="flex flex-col items-end leading-tight">
                    <span className="text-teal-200 font-semibold">{currentUserEntry.score}</span>
                    {currentUserEntry.questions_total ? (
                      <span className="text-[11px] text-teal-200/60">{currentUserEntry.questions_correct}/{currentUserEntry.questions_total}</span>
                    ) : null}
                  </span>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {error ? <p className="text-sm text-amber-200 mb-4 font-caudex">{error}</p> : null}
        {outOfAttempts ? (
          <p className="text-sm text-red-200 mb-4 font-caudex">
            You&apos;ve used all your attempts for this game.
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            onClick={onBack}
            className="px-5 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm font-caudex"
          >
            Back
          </button>
          <button
            onClick={handleStartClick}
            disabled={isStarting || outOfAttempts}
            className="px-6 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm font-caudex disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStarting ? 'Starting...' : 'Start Game'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomQuizLobby;